import React, { useState } from 'react';
import { Plus, Minus, HelpCircle, DollarSign, MousePointer, Clock, Calendar } from 'lucide-react';

const FAQSection = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  
  // Toggle the expanded state of a FAQ item
  const toggleExpand = (index) => {
    if (expandedIndex === index) {
      setExpandedIndex(null);
    } else {
      setExpandedIndex(index);
    }
  };
  
  // FAQ data with icons for visual enhancement
  const faqItems = [
    {
      id: 1,
      icon: <DollarSign />,
      question: "How does kinOS help me make more money?",
      answer: "By automating tasks, personalizing plans, and enabling digital products, kinOS helps you manage more clients and create new income streams. Coaches in our beta saw $5,000+ revenue increases through:\n\n• Handling more clients without increasing your workload\n• Selling digital products and courses automatically\n• Reducing time spent on admin tasks by 80%\n• Increasing client retention rates by 25%",
    },
    {
      id: 2,
      icon: <MousePointer />,
      question: "Is kinOS easy to use?",
      answer: "Yes! Our intuitive interface is designed specifically for health coaches, with no tech skills needed. Get started in minutes:\n\n• Simple step-by-step setup wizard\n• Pre-built templates for all your coaching needs\n• Free onboarding call with our support team\n• Comprehensive video tutorials for every feature",
    },
    {
      id: 3,
      icon: <DollarSign />,
      question: "What's the cost after the free trial?",
      answer: "Just $49/month, with a freemium option to try basic features forever. Our pricing is transparent with no hidden fees:\n\n• All features included in one simple plan\n• Unlimited clients and messaging\n• Free regular updates and new features\n• Cancel anytime with no long-term commitment",
    },
    {
      id: 4,
      icon: <Clock />,
      question: "How much time will I save using kinOS?",
      answer: "Most coaches save 10+ hours every week. kinOS automates your entire workflow:\n\n• Client acquisition and lead generation\n• Program creation and customization\n• Follow-ups and check-ins\n• Billing and administrative tasks\n• Content creation for social media",
    },
    {
      id: 5,
      icon: <HelpCircle />,
      question: "Do I need to be tech-savvy to use kinOS?",
      answer: "Not at all! kinOS was designed for coaches, not tech experts. Our platform is as easy to use as social media, with a clean interface and helpful prompts throughout. If you can use a smartphone, you can use kinOS effectively.",
    },
    {
      id: 6,
      icon: <HelpCircle />,
      question: "Can I integrate kinOS with my existing tools?",
      answer: "Absolutely! kinOS connects seamlessly with the tools you already use:\n\n• Calendar apps (Google Calendar, Outlook, Apple Calendar)\n• Payment processors (Stripe, PayPal)\n• Email marketing platforms (MailChimp, ConvertKit)\n• Video conferencing (Zoom, Google Meet)\n• Website builders (WordPress, Wix, Squarespace)",
    }
  ];
  
  return (
    <section id="faq" className="py-12 md:py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center mb-3 bg-[#c4ffa0]/20 px-3 py-1 rounded-full text-xs md:text-sm">
            <span className="mr-1 md:mr-2">❓</span>
            <span className="text-[#2d4a1e] font-medium">FREQUENTLY ASKED QUESTIONS</span>
            <span className="ml-1 md:ml-2">❓</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-3">Got Questions? We've Got Answers</h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto">
            Everything you need to know about kinOS and how it can transform your coaching business
          </p>
        </div>
        
        {/* FAQ tile grid - responsive layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {faqItems.map((item, index) => (
            <div 
              key={item.id}
              className="bg-white border border-gray-100 rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg"
            >
              {/* Question header with toggle button */}
              <div 
                className="p-5 md:p-6 flex items-center justify-between cursor-pointer"
                onClick={() => toggleExpand(index)}
              >
                <div className="flex items-center pr-4">
                  <div className="bg-[#c4ffa0]/10 rounded-full p-2 mr-3 text-[#2d4a1e]">
                    {React.cloneElement(item.icon, { className: 'w-5 h-5 text-[#2d4a1e]' })}
                  </div>
                  <h3 className="font-semibold text-base md:text-lg text-gray-800">{item.question}</h3>
                </div>
                <button 
                  className={`flex-shrink-0 p-1.5 rounded-full transition-colors ${
                    expandedIndex === index 
                      ? 'bg-[#c4ffa0]/20 text-[#2d4a1e]' 
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                  aria-label={expandedIndex === index ? "Collapse" : "Expand"}
                >
                  {expandedIndex === index ? <Minus size={18} /> : <Plus size={18} />}
                </button>
              </div>
              
              {/* Expandable answer section */}
              <div 
                className={`overflow-hidden transition-all duration-300 ${
                  expandedIndex === index 
                    ? 'max-h-96' 
                    : 'max-h-0'
                }`}
              >
                <div className="p-5 md:p-6 pt-0 border-t border-gray-100">
                  {item.answer.split('\n\n').map((paragraph, i) => (
                    <React.Fragment key={i}>
                      {paragraph.startsWith('•') ? (
                        <ul className="list-disc pl-5 mb-3 space-y-1">
                          {paragraph.split('\n').map((bulletPoint, j) => (
                            <li key={j} className="text-gray-600">
                              {bulletPoint.replace('• ', '')}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-600 mb-3">{paragraph}</p>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        
      
      </div>
      
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default FAQSection;